import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, Store, MapPin, Phone, Star, Trash2, Edit, Plus, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AdminLayout from "./AdminLayout";

export default function ShopsManagement() {
  const [shops, setShops] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingShop, setEditingShop] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    try {
      const [shopsRes, usersRes] = await Promise.all([
        api.get("/admin/shops"),
        api.get("/admin/users?role=seller")
      ]);
      setShops(shopsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleDelete = async (shopId, shopName) => {
    if (!window.confirm(`هل أنت متأكد من رغبتك بحذف متجر "${shopName}"؟ سيؤدي ذلك أيضاً لحذف منتجاته!`)) {
      return;
    }
    try {
      await api.delete(`/admin/shops/${shopId}`);
      toast.success("تم حذف المتجر بنجاح");
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleSave = async (shopData) => {
    setSaving(true);
    try {
      if (shopData.id) {
        await api.put(`/admin/shops/${shopData.id}`, shopData);
        toast.success("تم تحديث بيانات المتجر بنجاح");
      } else {
        await api.post("/admin/shops", shopData);
        toast.success("تم إنشاء المتجر بنجاح");
      }
      setIsDialogOpen(false);
      setEditingShop(null);
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const filteredShops = shops.filter(s =>
    !search ||
    (s.name && s.name.toLowerCase().includes(search.toLowerCase())) ||
    (s.category && s.category.toLowerCase().includes(search.toLowerCase())) ||
    (s.owner_name && s.owner_name.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <AdminLayout title="إدارة المتاجر" subtitle="مراقبة وإدارة المتاجر المحلية، وتعيين الملاك ورسوم التوصيل">
      <Card className="border border-border/80 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-lg">قائمة المتاجر المسجلة</CardTitle>
              <CardDescription>إجمالي المتاجر: {shops.length}</CardDescription>
            </div>
            <Button onClick={() => { setEditingShop(null); setIsDialogOpen(true); }} className="gap-2 shrink-0">
              <Plus className="w-4 h-4" />
              إضافة متجر جديد
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative mb-6">
            <Search className="absolute right-3 top-3 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="بحث باسم المتجر أو الفئة أو اسم البائع..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pr-9 max-w-md"
            />
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="space-y-2 md:hidden">
                {filteredShops.map((shop) => (
                  <article key={shop.id} className="rounded-2xl border border-border bg-card p-3.5">
                    <div className="flex items-start gap-3">
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><Store className="h-4 w-4" /></div>
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-sm font-extrabold">{shop.name || "-"}</h3>
                        <p className="mt-1 truncate text-xs text-muted-foreground">{shop.owner_name || "غير محدد"} · {shop.category || "عام"}</p>
                        <p className="mt-1 text-[10px] text-muted-foreground" dir="ltr">{shop.owner_phone || "-"}</p>
                      </div>
                      <span className={`shrink-0 text-[10px] font-bold ${shop.is_open ? "text-emerald-600" : "text-rose-600"}`}>{shop.is_open ? "مفتوح" : "مغلق"}</span>
                    </div>
                    <div className="mt-3 flex items-center justify-between border-t border-border pt-2">
                      <span className="text-xs text-muted-foreground">التوصيل: {shop.delivery_fee ? `${Number(shop.delivery_fee).toLocaleString()} ل.س` : "مجاني"}</span>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="h-9 gap-1 text-xs" onClick={() => { setEditingShop(shop); setIsDialogOpen(true); }}><Edit className="h-3.5 w-3.5" /> تعديل</Button>
                        <Button variant="outline" size="sm" className="h-9 gap-1 border-destructive/20 text-xs text-destructive" onClick={() => handleDelete(shop.id, shop.name)}><Trash2 className="h-3.5 w-3.5" /> حذف</Button>
                      </div>
                    </div>
                  </article>
                ))}
                {filteredShops.length === 0 && <div className="rounded-2xl bg-muted p-8 text-center text-sm text-muted-foreground">لا توجد أي متاجر مطابقة للبحث</div>}
              </div>
              <div className="hidden overflow-hidden rounded-xl border md:block">
                <table className="w-full text-sm text-right">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="p-3.5 font-semibold">المتجر</th>
                      <th className="p-3.5 font-semibold">المالك (البائع)</th>
                      <th className="p-3.5 font-semibold">الفئة</th>
                      <th className="p-3.5 font-semibold">رسوم التوصيل</th>
                      <th className="p-3.5 font-semibold">الحالة</th>
                      <th className="p-3.5 font-semibold">التقييم</th>
                      <th className="p-3.5 font-semibold text-center">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredShops.map((shop) => (
                      <tr key={shop.id} className="hover:bg-muted/30 transition-colors">
                        <td className="p-3.5 font-medium">
                          <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold text-sm shrink-0">
                              <Store className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="font-semibold text-foreground">{shop.name || "-"}</p>
                              <p className="text-[11px] text-muted-foreground line-clamp-1">{shop.description || "بدون وصف"}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3.5">
                          <p className="font-medium text-foreground text-xs">{shop.owner_name || "غير محدد"}</p>
                          <p className="text-[11px] text-muted-foreground font-mono" dir="ltr">{shop.owner_phone || "-"}</p>
                        </td>
                        <td className="p-3.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-muted font-medium">
                            {shop.category || "عام"}
                          </span>
                        </td>
                        <td className="p-3.5 font-mono text-xs">
                          {shop.delivery_fee ? `${Number(shop.delivery_fee).toLocaleString()} ل.س` : "مجاني"}
                        </td>
                        <td className="p-3.5">
                          {shop.is_open ? (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
                              <CheckCircle2 className="w-3.5 h-3.5" /> مفتوح
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-rose-600 font-medium">
                              <XCircle className="w-3.5 h-3.5" /> مغلق
                            </span>
                          )}
                        </td>
                        <td className="p-3.5">
                          <div className="flex items-center gap-1 text-xs font-semibold text-amber-600">
                            <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                            <span>{shop.rating ? Number(shop.rating).toFixed(1) : "5.0"}</span>
                          </div>
                        </td>
                        <td className="p-3.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              onClick={() => { setEditingShop(shop); setIsDialogOpen(true); }}
                              title="تعديل المتجر"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:bg-destructive/10"
                              onClick={() => handleDelete(shop.id, shop.name)}
                              title="حذف المتجر"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredShops.length === 0 && (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-muted-foreground text-sm">
                          لا توجد أي متاجر مطابقة للبحث
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Shop Modal Form */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent dir="rtl" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingShop ? "تعديل بيانات المتجر" : "إضافة متجر جديد"}</DialogTitle>
            <DialogDescription>
              {editingShop ? "تحديث إعدادات المتجر وبيانات التواصل وحالة الفتح" : "إنشاء متجر جديد وربطه بحساب بائع"}
            </DialogDescription>
          </DialogHeader>
          <ShopForm 
            shop={editingShop} 
            sellers={users}
            saving={saving}
            onSave={handleSave} 
            onClose={() => { setIsDialogOpen(false); setEditingShop(null); }} 
          />
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function ShopForm({ shop, sellers, saving, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "مطاعم",
    delivery_fee: 3000,
    owner_id: "",
    phone: "",
    is_open: true
  });

  useEffect(() => {
    if (shop) {
      setFormData({
        id: shop.id,
        name: shop.name || "",
        description: shop.description || "",
        category: shop.category || "مطاعم",
        delivery_fee: shop.delivery_fee ?? 3000,
        owner_id: shop.owner_id || "",
        phone: shop.phone || "",
        is_open: shop.is_open !== false
      });
    } else {
      setFormData({
        name: "",
        description: "",
        category: "مطاعم",
        delivery_fee: 3000,
        owner_id: sellers[0]?.id || "",
        phone: "+9639",
        is_open: true
      });
    }
  }, [shop, sellers]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pt-2">
      <div>
        <Label htmlFor="s_name">اسم المتجر *</Label>
        <Input
          id="s_name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="مثال: مطعم الياسمين"
          required
        />
      </div>
      <div>
        <Label htmlFor="s_desc">وصف المتجر</Label>
        <Input
          id="s_desc"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="أشهى المأكولات والوجبات السريعة"
        />
      </div>
      <div>
        <Label htmlFor="s_category">الفئة / التصنيف *</Label>
        <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
          <SelectTrigger id="s_category" dir="rtl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent dir="rtl">
            <SelectItem value="مطاعم">مطاعم ووجبات</SelectItem>
            <SelectItem value="مشروبات">حلويات ومشروبات</SelectItem>
            <SelectItem value="خضروات">خضروات وفواكه</SelectItem>
            <SelectItem value="لحوم">لحوم وقصابين</SelectItem>
            <SelectItem value="بقالة">بقالة ومواد غذائية</SelectItem>
            <SelectItem value="صيدلية">صيدليات ومستلزمات</SelectItem>
            <SelectItem value="عام">خدمات أخرى عامة</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="s_owner">مالك المتجر (البائع) *</Label>
        <Select value={formData.owner_id} onValueChange={(v) => setFormData({ ...formData, owner_id: v })}>
          <SelectTrigger id="s_owner" dir="rtl">
            <SelectValue placeholder="اختر حساب البائع" />
          </SelectTrigger>
          <SelectContent dir="rtl">
            {sellers.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name} ({s.email})
              </SelectItem>
            ))}
            {sellers.length === 0 && (
              <SelectItem value="none" disabled>
                لا يوجد بائعون مسجلون
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="s_fee">أجرة التوصيل (ل.س)</Label>
          <Input
            id="s_fee"
            type="number"
            value={formData.delivery_fee}
            onChange={(e) => setFormData({ ...formData, delivery_fee: Number(e.target.value) })}
            min={0}
            step={500}
          />
        </div>
        <div>
          <Label htmlFor="s_open">حالة استقبال الطلبات</Label>
          <Select value={String(formData.is_open)} onValueChange={(v) => setFormData({ ...formData, is_open: v === "true" })}>
            <SelectTrigger id="s_open" dir="rtl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent dir="rtl">
              <SelectItem value="true">مفتوح</SelectItem>
              <SelectItem value="false">مغلق مؤقتاً</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label htmlFor="s_phone">هاتف المتجر للتواصل</Label>
        <Input
          id="s_phone"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          placeholder="+9639XXXXXXXX"
          dir="ltr"
        />
      </div>

      <div className="flex gap-2 justify-end pt-4 border-t">
        <Button variant="outline" type="button" onClick={onClose} disabled={saving}>
          إلغاء
        </Button>
        <Button type="submit" disabled={saving}>
          {saving && <Loader2 className="w-4 h-4 ml-2 animate-spin" />}
          {shop ? "حفظ التعديلات" : "إضافة المتجر"}
        </Button>
      </div>
    </form>
  );
}
